import React, { useEffect } from 'react';
import cn from 'classnames';
import { useLocation } from '@reach/router';
import { observer, calculatorState as store } from 'store';
import Container from 'components/UI/Container';
import Subtitle from 'components/UI/Subtitle';
import Title from 'components/UI/Title';
import DesktopCalculator from './components/DesktopCalculator';
import MobileCalculator from './components/MobileCalculator';
import PreviewModal from './components/PreviewModal';
import { useInitialStateEffect, useToggleCalculators, usePrepareConfig } from './hooks';
import { useTrans } from 'utils/localisation';
import { useDefaultConfigs } from './configs';
import { withErrorBoundary } from 'components/ErrorBoundary';
import * as styles from './Calculator.module.scss';

import ResultModal from './components/ResultModal';

function Calculator({ containerId = 'calculator', config: initialConfig, type, title, subtitle }) {
  const location = useLocation();
  const hash = location.hash;
  const t = useTrans(['calculator']);
  const { deviceType } = useToggleCalculators();
  const defaultConfig = useDefaultConfigs(type);
  const config = usePrepareConfig(defaultConfig ?? initialConfig, deviceType);
  useInitialStateEffect(config.id, config.sections);
  const { hideMobile, counter } = config;

  const preparedTitle = title ?? config?.title;
  const preparedSubtitle = subtitle ?? config?.subtitle;

  const calculatorInitiated = Boolean(store.calculators[config.id]);

  useEffect(() => {
    const calculatorId = document.getElementById('calculator');

    if (calculatorId && hash === '#calculator') {
      calculatorId.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hash, config, deviceType]);

  const { formName, sections, id } = config;

  return (
    <Container
      id={containerId}
      className={hideMobile && cn(styles.hideMobile, styles.container)}
      padding={config.padding}
    >
      <div className={styles.wrapper}>
        {preparedTitle && (
          <Title tag="p" size="h2">
            {preparedTitle}
          </Title>
        )}
        {preparedSubtitle && <Subtitle size="lg">{preparedSubtitle}</Subtitle>}
        {calculatorInitiated && (
          <>
            {deviceType === 'desktop' && <DesktopCalculator {...config} />}
            {deviceType === 'mobile' && !hideMobile && <MobileCalculator {...config} />}
            <ResultModal
              counter={counter}
              deviceType={deviceType}
              calculatorID={id}
              formName={formName}
              sections={sections}
            />
            {deviceType === 'desktop' && (
              <PreviewModal title={t('previewTitle')} calculatorID={id} />
            )}
          </>
        )}
      </div>
    </Container>
  );
}

const ObservedCalculator = observer(Calculator);

ObservedCalculator.displayName = 'Calculator';

export default withErrorBoundary(ObservedCalculator);
